import React, { useEffect, useState } from 'react';
import {FaArrowLeft, FaChevronDown, FaChevronUp, FaFileDownload, FaSpinner, FaWarehouse} from "react-icons/fa";
import { Link, useParams } from 'react-router-dom';
import { downloadService } from '../api/downloadService';
import { orderService } from '../api/orderService';
import { CustomOption, Order } from '../types/order';
import { Tooltip } from 'react-tooltip';
import {productService} from "../api/productService";
import {toast} from "react-toastify";

interface OrderDetailsProps { }

const OrderDetails: React.FC<OrderDetailsProps> = () => {
    const { commandeId } = useParams<{ commandeId: string }>();
    const [order, setOrder] = useState<Order | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [openAccordion, setOpenAccordion] = useState<number | null>(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isDownloading, setIsDownloading] = useState<string | null>(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollableContent = document.getElementById('scrollable-content');
            if (scrollableContent) {
                setIsScrolled(scrollableContent.scrollTop > 0);
            }
        };

        const scrollableContent = document.getElementById('scrollable-content');
        if (scrollableContent) {
            scrollableContent.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (scrollableContent) {
                scrollableContent.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    const downloadFile = async (fileType: string) => {
        try {
            await downloadService.downloadFile(
                `/download/${commandeId}/${fileType}`,
                `order-${commandeId}.${fileType}`,
                fileType
            );
        } catch (error) {
            // Error handling is done in the service
        }
    };

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!commandeId) return;
            try {
                const fetchedOrder = await orderService.fetchOrderById(commandeId);
                setOrder(fetchedOrder);
            } catch (err) {
                setError('Erreur lors du chargement des détails de la commande');
            }
        };

        fetchOrderDetails();
    }, [commandeId]);

    const renderProductCustomization = (productOptions: any) => {
        const { customOptions, bundleOptions } = productOptions;

        const getOptionSelectionDetails = (selection: any) => {
            const name = selection.name || selection.title || selection.label;
            const id = selection.id || selection.optionId || 'ID inconnu';
            return name ? `${name} (ID: ${id})` : null;
        };

        return (
            <div className="mt-4">
                {customOptions && customOptions.length > 0 && (
                    <div className="mb-4">
                        <h4 className="text-md font-semibold text-gray-700 mb-2">
                            Options personnalisées :
                        </h4>
                        <ul className="list-disc list-inside space-y-1">
                            {customOptions
                                .sort((a: CustomOption, b: CustomOption) => a.optionLabel.localeCompare(b.optionLabel))
                                .map((option: CustomOption, index: number) => {
                                    // TODO URL dynamique suivant le contexte
                                    const linkUrl = `https://labelmaker.ludilabel.fr/labelmaker/personalisation-bo-edit?persoId=${encodeURIComponent(option.optionValue)}`;
                                    const linkUrlXml = `https://labelmaker.ludilabel.fr/api/V2/personalization/${encodeURIComponent(option.optionValue)}/data/xml`;
                                    const linkUrlResume = `https://labelmaker.ludilabel.fr/api/V2/personalization/${encodeURIComponent(option.optionValue)}/data/fabsheet`;
                                    return (
                                        <li key={index} className="text-sm text-gray-600">
                                            <span className="font-medium text-gray-800">{option.optionLabel} : </span>
                                            {option.optionLabel === "labelmaker-personalization-id"
                                                ? (
                                                    <span>
                                                        <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                                                            ✏️ Modifier la perso #{option.optionValue}
                                                        </a>
                                                        <a href={linkUrlXml} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                                                            ✏️ Voir XML
                                                        </a>
                                                        <a href={linkUrlResume} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                                                            ✏️ Voir résumé
                                                        </a>
                                                    </span>
                                                )
                                                : (
                                                    option.optionValue
                                                )
                                            }
                                            {option.value}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const renderSubItems = (parentItemId: string) => {
        const subItems = order?.orderItems.filter(item => item.parentItem && item.parentItem['@id'] === parentItemId);

        if (!subItems?.length) return null;

        return (
            <div className="mt-4">
                <h4 className="text-md font-semibold text-gray-700 mb-2">
                    Sous-produits
                </h4>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[60%]">
                                Nom
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[30%]">
                                SKU
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                                Quantité
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {subItems.map((subItem, index) => (
                            <tr key={index} className="even:bg-gray-50">
                                <td className="px-6 py-4 text-sm text-gray-900">
                                    <div
                                        className="truncate"
                                        data-tooltip-id="cell-tooltip"
                                        data-tooltip-content={subItem.name}
                                    >
                                        {subItem.name}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">
                                    <div
                                        className="truncate"
                                        data-tooltip-id="cell-tooltip"
                                        data-tooltip-content={subItem.sku}
                                    >
                                        {subItem.sku}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 text-center w-[80px]">
                                    {subItem.qtyOrdered}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <Tooltip
                        id="cell-tooltip"
                        place="top"
                        className="z-50"
                        style={{
                            backgroundColor: '#1F2937',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            maxWidth: '300px'
                        }}
                    />
                </div>
            </div>
        );
    };

    const toggleAccordion = (index: number) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    const handleDownload = async (fileType: string) => {
        setIsDownloading(fileType);
        try {
            await downloadFile(fileType);
        } finally {
            setIsDownloading(null);
        }
    };

    const generateZip = async () => {
        if (!order || !commandeId) return;

        try {
            await orderService.generateZip(commandeId);
            toast.success('ZIP envoyé avec succès!');
        } catch (err) {
            setError('Erreur lors de l\'envoi du ZIP');
            toast.error('Erreur lors de l\'envoi du ZIP');
        }
    };

    const generateXml = async () => {
        if (!order || !commandeId) return;

        try {
            await orderService.generateXml(commandeId);
            toast.success('XML envoyé avec succès!');
        } catch (err) {
            setError('Erreur lors de l\'envoi du XML');
            toast.error('Erreur lors de l\'envoi du XML');
        }
    };

    const generatePdf = async () => {
        if (!order || !commandeId) return;

        try {
            await orderService.generatePdf(commandeId);
            toast.success('PDF envoyé avec succès!');
        } catch (err) {
            setError('Erreur lors de l\'envoi du PDF');
            toast.error('Erreur lors de l\'envoi du PDF');
        }
    };


    if (error) {
        return <div>Erreur: {error}</div>;
    }

    if (!order) {
        return <div>Commande non trouvée</div>;
    }

    return (
        <div className="flex flex-col h-screen">
            {/* En-tête fixe avec ombre conditionnelle */}
            <div
                className={`shrink-0 bg-white z-10 transition-shadow duration-200 ${
                    isScrolled ? 'shadow-lg' : ''
                }`}
            >
                <div className="container mx-auto max-w-7xl p-4">
                    <h2 className="mb-4 break-words text-2xl font-semibold text-gray-900 sm:text-3xl lg:mb-6">
                        Détails de la Commande #{order?.remoteId}
                    </h2>

                    <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                        <Link to="/commandes" className="inline-flex items-center text-blue-600 hover:text-blue-800">
                            <FaArrowLeft className="mr-2" /> Retour à la liste des commandes
                        </Link>
                        <div className="flex w-full flex-col gap-3 xl:w-auto 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="grid w-full grid-cols-3 gap-2 2xl:flex 2xl:w-auto">
                                <button
                                    onClick={() => handleDownload('xml')}
                                    disabled={isDownloading === 'xml'}
                                    aria-label="Télécharger XML"
                                    className={`
                    inline-flex w-full min-w-0 items-center justify-center gap-1 rounded px-2 py-2 text-sm font-medium sm:gap-2 sm:px-4 2xl:w-auto
                    ${isDownloading === 'xml'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#272860] hover:bg-[#1a1b45]'
                                    }
                    text-white transition-colors duration-200
                  `}
                                >
                                    {isDownloading === 'xml' ? (
                                        <FaSpinner className="shrink-0 animate-spin" />
                                    ) : (
                                        <FaFileDownload className="shrink-0" />
                                    )}
                                    <span className="sm:hidden">XML</span>
                                    <span className="hidden sm:inline">Télécharger XML</span>
                                </button>

                                <button
                                    onClick={() => handleDownload('pdf')}
                                    disabled={isDownloading === 'pdf'}
                                    aria-label="Télécharger PDF"
                                    className={`
                    inline-flex w-full min-w-0 items-center justify-center gap-1 rounded px-2 py-2 text-sm font-medium sm:gap-2 sm:px-4 2xl:w-auto
                    ${isDownloading === 'pdf'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#272860] hover:bg-[#1a1b45]'
                                    }
                    text-white transition-colors duration-200
                  `}
                                >
                                    {isDownloading === 'pdf' ? (
                                        <FaSpinner className="shrink-0 animate-spin" />
                                    ) : (
                                        <FaFileDownload className="shrink-0" />
                                    )}
                                    <span className="sm:hidden">PDF</span>
                                    <span className="hidden sm:inline">Télécharger PDF</span>
                                </button>

                                <button
                                    onClick={() => handleDownload('zip')}
                                    disabled={isDownloading === 'zip'}
                                    aria-label="Télécharger ZIP"
                                    className={`
                    inline-flex w-full min-w-0 items-center justify-center gap-1 rounded px-2 py-2 text-sm font-medium sm:gap-2 sm:px-4 2xl:w-auto
                    ${isDownloading === 'zip'
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#272860] hover:bg-[#1a1b45]'
                                    }
                    text-white transition-colors duration-200
                  `}
                                >
                                    {isDownloading === 'zip' ? (
                                        <FaSpinner className="shrink-0 animate-spin" />
                                    ) : (
                                        <FaFileDownload className="shrink-0" />
                                    )}
                                    <span className="sm:hidden">ZIP</span>
                                    <span className="hidden sm:inline">Télécharger ZIP</span>
                                </button>
                            </div>
                            <div className="grid w-full grid-cols-3 gap-2 2xl:flex 2xl:w-auto">
                                <button
                                    onClick={() => {
                                        if (window.confirm("Êtes-vous sûr de vouloir renvoyer le XML en production ?")) {
                                            generateXml();
                                        }
                                    }}
                                    aria-label="Renvoyer XML en production"
                                    className="inline-flex w-full min-w-0 items-center justify-center rounded-md border border-transparent gap-2 px-2 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:px-4 2xl:w-auto bg-red-600"
                                >
                                    <FaWarehouse className="shrink-0" />
                                    <span className="sm:hidden">XML</span>
                                    <span className="hidden sm:inline">Renvoyer XML en prod</span>
                                </button>
                                <button
                                    onClick={() => {
                                        if (window.confirm("Êtes-vous sûr de vouloir renvoyer le PDF en production ?")) {
                                            generatePdf();
                                        }
                                    }}
                                    aria-label="Renvoyer PDF en production"
                                    className="inline-flex w-full min-w-0 items-center justify-center rounded-md border border-transparent gap-2 px-2 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:px-4 2xl:w-auto bg-red-600"
                                >
                                    <FaWarehouse className="shrink-0" />
                                    <span className="sm:hidden">PDF</span>
                                    <span className="hidden sm:inline">Renvoyer PDF en prod</span>
                                </button>
                                <button
                                    onClick={() => {
                                        if (window.confirm("Êtes-vous sûr de vouloir renvoyer le ZIP en production ?")) {
                                            generateZip();
                                        }
                                    }}
                                    aria-label="Renvoyer ZIP en production"
                                    className="inline-flex w-full min-w-0 items-center justify-center rounded-md border border-transparent gap-2 px-2 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:px-4 2xl:w-auto bg-red-600"
                                >
                                    <FaWarehouse className="shrink-0" />
                                    <span className="sm:hidden">ZIP</span>
                                    <span className="hidden sm:inline">Renvoyer ZIP en prod</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenu scrollable */}
            <div
                id="scrollable-content"
                className="flex-1 overflow-y-auto"
            >
                <div className="container mx-auto max-w-7xl p-4">
                    <div className="bg-white shadow-xl rounded-xl p-4 space-y-6 sm:p-6">
                        {/* Informations générales */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <span className="mr-2">📋</span>
                                Informations générales
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">ID interne</p>
                                    <p className="font-medium">{order.internalId}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Email client</p>
                                    <p className="font-medium">{order.customerEmail}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Date de commande</p>
                                    <p className="font-medium">{new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString()}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Statut</p>
                                    <p className="font-medium">{order.status}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Adresse de livraison</p>
                                    <p className="font-medium">
                                        {order.shippingAddress?.street}<br />
                                        {order.shippingAddress?.postcode} {order.shippingAddress?.city}<br />
                                        {order.shippingAddress?.countryId}
                                    </p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-500 mb-1">Total</p>
                                    <p className="font-medium">{order.baseGrandTotal} €</p>
                                </div>
                            </div>
                        </div>

                        {/* Articles commandés */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <span className="mr-2">🛍️</span>
                                Articles Commandés
                            </h3>
                            {order.orderItems.filter(item => !item.parentItem).map((item, index) => (
                                <div key={index} className="mb-4">
                                    <div
                                        className="flex flex-col gap-3 p-4 border rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 sm:flex-row sm:items-center sm:justify-between"
                                        onClick={() => toggleAccordion(index)}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <span className="break-words font-semibold text-gray-700">{item.name}</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-600 sm:justify-end">
                                            <span className="whitespace-nowrap">Qté: {item.qtyOrdered}</span>
                                            <span className="whitespace-nowrap">Prix total: {(item.basePriceInclTax * item.qtyOrdered).toFixed(2)} €</span>
                                            {openAccordion === index ? <FaChevronUp className="ml-auto sm:ml-0" /> : <FaChevronDown className="ml-auto sm:ml-0" />}
                                        </div>
                                    </div>
                                    {openAccordion === index && (
                                        <div className="p-4 border-x border-b rounded-b-lg space-y-2">
                                            <p className="text-gray-600">SKU: {item.sku}</p>
                                            <p className="text-gray-600">Id Produit: {item.productId}</p>
                                            <p className="text-gray-600">Prix unitaire: {item.basePriceInclTax} €</p>
                                            <p className="text-gray-600">Taxe: {item.taxAmount} €</p>
                                            {item.productOption && renderProductCustomization(item.productOption)}
                                            {renderSubItems(item['@id'])}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetails; 